import * as React from 'react';

import { toMoney } from '@rematter/common/money';
import { CurrencyCode } from '@rematter/frontend-lib-api-client';

import { FinanceLineItemData, FinanceLineItemErrors } from '../types';

import { PriceFieldCell } from '~/shared/components/line-items';

const FORMAT_OPTIONS: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

interface UnitPriceFieldCellProps {
  lineItem: FinanceLineItemData;
  currencyCode: CurrencyCode;
  onUpdate: (id: string, updates: Partial<FinanceLineItemData>) => void;
  disabled?: boolean;
  errors?: FinanceLineItemErrors;
  allowNegative?: boolean;
}

export const UnitPriceFieldCell: React.FC<UnitPriceFieldCellProps> = ({
  lineItem,
  currencyCode,
  onUpdate,
  disabled = false,
  errors,
  allowNegative = false,
}) => {
  const handleChange = React.useCallback(
    (value: number) => {
      const quantity = lineItem.quantity ?? 0;
      const totalAmount = quantity === 0 ? value : quantity * value;

      onUpdate(lineItem.id, {
        unitPrice: toMoney({
          amount: value,
          currencyCode,
        }),
        totalPrice: toMoney({
          amount: totalAmount,
          currencyCode,
        }),
      });
    },
    [lineItem.id, lineItem.quantity, currencyCode, onUpdate],
  );

  return (
    <PriceFieldCell
      value={lineItem.unitPrice?.amount ?? 0}
      onChange={handleChange}
      disabled={disabled}
      minValue={allowNegative ? Number.MIN_SAFE_INTEGER : 0}
      formatOptions={FORMAT_OPTIONS}
      errorMessage={errors?.unitPrice}
      isInvalid={!!errors?.unitPrice}
      className="@UnitPriceFieldCell"
    />
  );
};
